import { Fragment } from 'react';
import { Box, Typography, Grid, FormControl, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import EquipmentImage from './EquipmentImage';

const fontFamily = '"Inter", sans-serif';

const ProposalViewSystemEquipment = ({ 
  arr, 
  handleChange, 
  system_index, 
  title,
  equipmentTypes = {} 
}) => (
  <Fragment>
    <Typography
      sx={{
        fontFamily,
        fontWeight: 600,
        fontSize: 14,
        color: '#1a1a1a',
        pb: 1.5,
        mb: 0,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderBottom: '2px solid #0d3c61',
      }}
    >
      {title || 'Equipment'}
    </Typography>

    <Grid container spacing={2}>
      {arr.map((el, idx) => {
        // Handle flat equipment items (from public/SMS views) without flexibleItems
        if (!el.flexibleItems) {
          return (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Box
                sx={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: 1,
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {el.imageUrl && (
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                    <EquipmentImage src={el.imageUrl} alt={el.modelNumber || el.equipmentName} />
                  </Box>
                )}
                <Typography sx={{ fontFamily, fontSize: 12, fontWeight: 600, color: '#0d3c61', mb: 0.5 }}>
                  {el.manufacturerName || el.equipmentType || ''}
                </Typography>
                <Typography sx={{ fontFamily, fontSize: 14, fontWeight: 500, color: '#1a1a1a', mb: 1 }}>
                  {el.modelNumber || el.equipmentName || ''}
                </Typography>
                {el.description && (
                  <Typography sx={{ fontFamily, fontSize: 12, color: '#666', lineHeight: 1.5, flex: 1 }}>
                    {el.description}
                  </Typography>
                )}
              </Box>
            </Grid>
          );
        }

        if (el.flexibleItems.length === 0) return null;

        return (
          <Fragment key={idx}>
            {el.flexibleItems.length < 2 ? (
              // Single item - display as card
              <Grid item xs={12} sm={6} md={4}>
                <Box
                  sx={{
                    backgroundColor: '#f8f9fa',
                    borderRadius: 1,
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {(el.flexibleItems[0].item.imageUrl || equipmentTypes[el.flexibleItems[0].item.type]) && (
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                      <EquipmentImage
                        src={el.flexibleItems[0].item.imageUrl || equipmentTypes[el.flexibleItems[0].item.type]}
                        alt={el.flexibleItems[0].item.model}
                        sx={{ maxHeight: 120, width: 'auto', objectFit: 'contain' }}
                      />
                    </Box>
                  )}
                  <Typography sx={{ fontFamily, fontSize: 12, fontWeight: 600, color: '#0d3c61', mb: 0.5 }}>
                    {el.flexibleItems[0].item.make}
                  </Typography>
                  <Typography sx={{ fontFamily, fontSize: 14, fontWeight: 500, color: '#1a1a1a', mb: 1 }}>
                    {el.flexibleItems[0].item.model}
                    {el.flexibleItems[0].item.size && ` - ${el.flexibleItems[0].item.size}`}
                  </Typography>
                  {el.flexibleItems[0].item.description && (
                    <Typography sx={{ fontFamily, fontSize: 12, color: '#666', lineHeight: 1.5, flex: 1 }}>
                      {el.flexibleItems[0].item.description}
                    </Typography>
                  )}
                </Box>
              </Grid>
            ) : (
              // Multiple options - show radio selection
              <Grid item xs={12}>
                <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, p: 2 }}>
                  <Typography sx={{ fontFamily, fontSize: 12, color: '#666', mb: 1 }}>
                    Select an option:
                  </Typography>
                  <FormControl fullWidth>
                    <RadioGroup
                      value={el.selectedItemIndex}
                      onChange={(event) => handleChange(event, system_index, 'equipments', el)}
                    >
                      {el.flexibleItems.map((item, item_index) => (
                        <FormControlLabel
                          value={item_index}
                          key={item_index}
                          control={
                            <Radio 
                              size="small" 
                              sx={{ color: '#0d3c61', '&.Mui-checked': { color: '#0d3c61' } }} 
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                              {(item.item.imageUrl || equipmentTypes[item.item.type]) && (
                                <EquipmentImage
                                  src={item.item.imageUrl || equipmentTypes[item.item.type]}
                                  alt={item.item.model}
                                  sx={{ width: 60, height: 60, objectFit: 'contain' }}
                                />
                              )}
                              <Box>
                                <Typography sx={{ fontFamily, fontSize: 13, fontWeight: 500, color: '#1a1a1a' }}>
                                  {item.item.type} {item.item.make} {item.item.model}
                                </Typography>
                                {item.item.description && (
                                  <Typography sx={{ fontFamily, fontSize: 11, color: '#666' }}>
                                    {item.item.description}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          }
                          sx={{
                            m: 0, py: 0.5,
                            borderBottom: item_index < el.flexibleItems.length - 1 ? '1px solid #e0e0e0' : 'none',
                            alignItems: 'flex-start',
                          }}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Grid>
            )}
          </Fragment>
        );
      })}
    </Grid>
  </Fragment>
);

export default ProposalViewSystemEquipment;
